import { Dispatch } from 'react'
import Web3, { AbiFragment } from 'web3'
import axios from 'axios'
import { axiosInstance } from 'src/util/axios'
import { txUpdateType } from 'src/pages/AirDrop/types'
import { chainsDataType, networkType, networksData } from 'src/util/chainsData'
import { tokenSelectObjectType } from 'src/util/types'

export const getEvmTokenDecimals = async (
  contractAddress: string,
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  const web3 = new Web3(
    new Web3.providers.HttpProvider(networksData[chain][network].rpc[rpcRequestCount]),
  )
  incrementRpcCount()
  const erc20Contract = new web3.eth.Contract(
    networksData[chain].abi as AbiFragment[],
    contractAddress,
  )
  const decimals = await erc20Contract.methods.decimals().call()
  return Number(decimals)
}

export const getTransactionsData = async (dispatch: Dispatch<any>, address: string) => {
  try {
    const response = await axiosInstance.post('/blockchain-helpers/getAllTxForAddress', {
      address,
    })
    dispatch({
      type: 'TXDATA',
      payload: response?.data?.data,
    })
  } catch (error) {
    console.error(error)
    return error
  }
}

export const getBulkData = async () => {
  try {
    const response = await axiosInstance.post('/blockchain-helpers/getAllBulkTransactions')
    return response?.data?.data
  } catch (error) {
    console.error(error)
    return error
  }
}
export const getAllBulkById = async (id: string) => {
  try {
    const response = await axiosInstance.post('/blockchain-helpers/getAllBulkById', { id })
    return response?.data?.data
  } catch (error) {
    console.error(error)
    return error
  }
}

export const updateTransactions = async (payload: txUpdateType[]) => {
  try {
    await axiosInstance.post('/blockchain-helpers/updateBulkTxStatus', {
      transactions: payload,
    })
  } catch (error) {
    console.error(error)
    return error
  }
}

export const removeSelectedTransactions = async (payload: any, bulkId: string) => {
  try {
    await axiosInstance.post('/blockchain-helpers/deleteMultipleDetailedTransaction', {
      multipleId: payload,
    })

    const res = await getAllBulkById(bulkId)
    return res?.transactions
  } catch (error) {
    console.error(error)
    return error
  }
}
export const removeSelectedAidrops = async (payload: any) => {
  try {
    const response = await axiosInstance.post('/blockchain-helpers/deleteBulkTransaction', {
      multipleId: payload,
    })

    return response?.data?.data
  } catch (error) {
    console.error(error)
    return error
  }
}

export const sendBulkTransactions = async (
  payload: any,
  initiator: string,
  operation: string,
  network: string,
  chain: string,
  token: tokenSelectObjectType,
) => {
  try {
    const bulkTransactionsData = {
      initiator,
      operation,
      chain: chain.toLowerCase(),
      network,
      transactions: payload,
      token: {
        identifier: token?.tokenIdentifier,
        name: token?.name,
      },
    }
    if (chain.toLowerCase() === 'ethereum') {
      // @ts-ignore
      bulkTransactionsData.chainId = networksData[chain.toLowerCase()][network].chainId
    }
    const response = await axiosInstance.post(
      '/blockchain-helpers/addBulkTransaction',
      bulkTransactionsData,
    )
    return response?.data?.data
  } catch (error) {
    console.error(error)
    return error
  }
}
export const addMultipleDetailedTransactions = async (
  payload: any,
  id: string,
  network: string,
) => {
  payload.map((tx: any) => {
    tx.bulkTransactionId = id
    tx.network = network
  })
  try {
    const response = await axiosInstance.post(
      '/blockchain-helpers/addMultipleDetailedTransactions',
      payload,
    )

    return response?.data?.data
  } catch (error) {
    console.error(error)
    return error
  }
}

export const storeSigner = async (dispatch: Dispatch<any>, payload: any) => {
  dispatch({
    type: 'SIGNER',
    payload,
  })
}

export const storeInitiatorAddress = async (dispatch: Dispatch<any>, payload: string) => {
  dispatch({
    type: 'INITIATOR',
    payload,
  })
}

export const addToken = async (
  tokenName: string,
  tokenIdentifier: string,
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  try {
    const decimals = await getEvmTokenDecimals(
      tokenIdentifier,
      chain,
      network,
      rpcRequestCount,
      incrementRpcCount,
    )
    const response = await axiosInstance.post('/blockchain-helpers/addUserAsset', {
      operation: 'token',
      name: tokenName,
      tokenIdentifier,
      chain,
      network,
      decimals,
    })

    return response?.data?.data
  } catch (error) {
    console.error(error)
    return error
  }
}

export const setSignXPortal = async (dispatch: Dispatch<any>, payload: Boolean) => {
  dispatch({
    type: 'SIGNXPORTAL',
    payload,
  })
}

export const setExternalWallet = async (dispatch: Dispatch<any>, payload: Boolean) => {
  dispatch({
    type: 'EXTERNALWALLET',
    payload,
  })
}

export const getUserTokens = async (
  chain: string,
  address: string,
  network: networkType,
): Promise<tokenSelectObjectType[]> => {
  try {
    let tokens: tokenSelectObjectType[] = []
    if (chain === 'multiversx') {
      const multiversxApi =
        process.env.REACT_APP_ENVIRONMENT === 'production'
          ? 'https://api.multiversx.com	'
          : 'https://devnet-api.multiversx.com'
      const response = await axios.get(`${multiversxApi}/accounts/${address}/tokens?size=150`)
      tokens = response?.data?.map((token: any) => {
        return {
          tokenIdentifier: token.identifier,
          name: token.name,
          decimals: token.decimals,
          chain,
          network,
          operation: 'token',
        }
      })
    } else {
      const response = await axiosInstance.post('/blockchain-helpers/getTokenAssets', {
        chain,
      })
      tokens = response?.data?.data
    }

    return tokens
  } catch (error) {
    console.error(error)
    return []
  }
}

export const getMvxTokenDetails = async (tokenIdentifier: string): Promise<any> => {
  try {
    const multiversxApi =
      process.env.REACT_APP_ENVIRONMENT === 'production'
        ? 'https://api.multiversx.com	'
        : 'https://devnet-api.multiversx.com'
    const response = await axios.get(`${multiversxApi}/tokens/${tokenIdentifier}`)

    return response.data?.decimals
  } catch (error) {
    console.error(error)
    return []
  }
}
